import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Save, Trash2, AlertCircle, CheckCircle2, Link2, Upload, Youtube, FileText } from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTheme } from "../../hooks/useTheme";
import api from "../../api/api";

// Manages the Getting Started + Tutorials help content (migration 040
// dbo.Resources) through /admin/resources. Placement decides which page a
// row shows on; Source (link / file / youtube) decides how the user opens it.
const PLACEMENTS = [
  { value: "getting-started", label: "Getting Started" },
  { value: "tutorials", label: "Tutorials" },
];
const TYPES = ["video", "blog", "guide", "link"];
const SOURCES = [
  { value: "link", label: "Link", Icon: Link2 },
  { value: "file", label: "Upload", Icon: Upload },
  { value: "youtube", label: "YouTube", Icon: Youtube },
];
const emptyDraft = { title: "", type: "guide", placement: "getting-started", source: "link", url: "", file: null };

// Same rule as the backend (resources_service._check_link): a web address with
// http:// or https:// and no spaces; YouTube can also be a bare 11-character id
const linkError = (source, url) => {
  const u = (url || "").trim();
  if (!u) return "Every link or YouTube resource needs a link.";
  if (source === "youtube" && /^[\w-]{11}$/.test(u)) return null;
  if (!/^https?:\/\/\S+$/i.test(u)) {
    return source === "youtube"
      ? "A YouTube link has to start with http:// or https:// (or be an 11-character video id)."
      : "A link has to start with http:// or https:// and have no spaces.";
  }
  return null;
};

const AdminResources = ({ embedded = false }) => {
  const { isDarkMode } = useTheme();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const noticeTimer = useRef(null);

  const skBase = isDarkMode ? "#334155" : "#e2e8f0";
  const skHi = isDarkMode ? "#475569" : "#f1f5f9";

  const flash = useCallback((type, text) => {
    setNotice({ type, text });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 4000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/resources");
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list.map((it) => ({ ...it, dirty: false })));
    } catch {
      setItems([]);
      flash("error", "Couldn’t load resources.");
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const addResource = async () => {
    const title = draft.title.trim();
    if (!title || adding) return;
    if (draft.source === "file" && !draft.file) { flash("error", "Choose a file to upload."); return; }
    if (draft.source !== "file" && !draft.url.trim()) { flash("error", "Enter a link."); return; }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("type", draft.type);
      fd.append("placement", draft.placement);
      fd.append("source", draft.source);
      if (draft.source === "file") fd.append("file", draft.file);
      else fd.append("url", draft.url.trim());
      // The shared axios instance defaults Content-Type to application/json, which
      // breaks this FormData upload (the server can't parse the multipart body, so
      // every field — including title — arrives empty → "Title is required"). Unset
      // it so the browser sets multipart/form-data with the correct boundary.
      await api.post("/admin/resources", fd, { headers: { "Content-Type": undefined } });
      setDraft(emptyDraft);
      await load();
      flash("success", "Resource added and now live.");
    } catch (err) {
      flash("error", err?.response?.data?.error || "Couldn’t add the resource.");
    } finally {
      setAdding(false);
    }
  };

  const updateItem = (id, field, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value, dirty: true } : it)));
  };

  const dirtyItems = items.filter((it) => it.dirty);

  const saveAll = async () => {
    if (!dirtyItems.length || saving) return;
    if (dirtyItems.some((it) => !(it.title || "").trim())) { flash("error", "Every resource needs a title."); return; }
    // an uploaded file has no link to check
    const badLink = dirtyItems.map((it) => (it.source === "file" ? null : linkError(it.source, it.url))).find(Boolean);
    if (badLink) { flash("error", badLink); return; }
    setSaving(true);
    try {
      await Promise.all(dirtyItems.map((it) =>
        api.put(`/admin/resources/${it.id}`, {
          title: (it.title || "").trim(),
          type: it.type,
          placement: it.placement,
          url: it.url || "",
          sortOrder: it.sortOrder,
        })
      ));
      setItems((prev) => prev.map((it) => ({ ...it, dirty: false })));
      flash("success", "Changes saved.");
    } catch (err) {
      flash("error", err?.response?.data?.error || "Couldn’t save changes.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const id = pendingDelete;
    if (id === null || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/resources/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      flash("success", "Resource removed.");
    } catch (err) {
      flash("error", err?.response?.data?.error || "Couldn’t remove the resource.");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const cardCls = `rounded-2xl border shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500" : "bg-white border-slate-300 text-slate-800 placeholder-slate-400"}`;
  const labelCls = `text-xs font-bold uppercase tracking-wide mb-1 block ${isDarkMode ? "text-slate-400" : "text-slate-500"}`;
  const srcLabel = { link: "Link", file: "File", youtube: "YouTube" };

  return (
    <div className={`${embedded ? "w-full" : `min-h-screen w-full ${isDarkMode ? "bg-surface text-slate-100" : "bg-surface text-slate-900"}`}`}>
      <div className={`${embedded ? "p-4 space-y-6" : "max-w-6xl mx-auto px-4 py-8 space-y-6"}`}>

        {notice && (
          <div role="alert" className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
            notice.type === "error"
              ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
              : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
          }`}>
            {notice.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
            {notice.text}
          </div>
        )}

        {/* Add resource */}
        <div className={`${cardCls} p-6`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Add Resource</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className={labelCls}>Title</label>
              <input type="text" value={draft.title} onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))} placeholder="Resource title" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select value={draft.type} onChange={(e) => setDraft((p) => ({ ...p, type: e.target.value }))} className={inputCls}>
                {TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Placement</label>
              <select value={draft.placement} onChange={(e) => setDraft((p) => ({ ...p, placement: e.target.value }))} className={inputCls}>
                {PLACEMENTS.map((pl) => <option key={pl.value} value={pl.value}>{pl.label}</option>)}
              </select>
            </div>
          </div>

          {/* Source toggle */}
          <div className="mt-3">
            <label className={labelCls}>Source</label>
            <div className={`inline-flex rounded-lg border p-1 gap-1 ${isDarkMode ? "border-slate-600 bg-slate-900" : "border-slate-300 bg-slate-50"}`}>
              {SOURCES.map(({ value, label, Icon }) => (
                <button key={value} type="button" onClick={() => setDraft((p) => ({ ...p, source: value, url: "", file: null }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                    draft.source === value ? "bg-blue-600 text-white" : isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-white"
                  }`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Conditional input */}
          <div className="mt-3">
            {draft.source === "file" ? (
              <label className={`flex items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm cursor-pointer ${isDarkMode ? "border-slate-600 bg-slate-900 text-slate-300" : "border-slate-300 bg-slate-50 text-slate-600"}`}>
                <Upload size={16} />
                <span>{draft.file ? draft.file.name : "Choose a file — PDF, PNG, JPG up to 20MB"}</span>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setDraft((p) => ({ ...p, file: e.target.files?.[0] || null }))} />
              </label>
            ) : (
              <input type="url" value={draft.url} onChange={(e) => setDraft((p) => ({ ...p, url: e.target.value }))}
                placeholder={draft.source === "youtube" ? "YouTube link or video id" : "https://…"} className={inputCls} />
            )}
          </div>

          <div className="flex justify-end mt-4">
            <button type="button" onClick={addResource} disabled={!draft.title.trim() || adding}
              className="rounded-lg px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
              <Plus size={16} /> {adding ? "Adding…" : "Add Resource"}
            </button>
          </div>
        </div>

        {/* List */}
        <div className={`${cardCls} overflow-hidden`}>
          <div className={`hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b text-xs font-semibold uppercase ${isDarkMode ? "border-slate-700 text-slate-400" : "border-slate-200 text-slate-500"}`}>
            <div className="col-span-5">Title</div>
            <div className="col-span-3">Placement</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-1">Source</div>
            <div className="col-span-1 text-right">Delete</div>
          </div>

          <div className={`divide-y ${isDarkMode ? "divide-slate-700/60" : "divide-slate-200/60"}`}>
            {loading ? (
              <SkeletonTheme baseColor={skBase} highlightColor={skHi}>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex flex-col gap-3 px-4 py-3 md:grid md:grid-cols-12 md:gap-3 md:items-center">
                    <div className="md:col-span-5"><Skeleton height={38} borderRadius={8} /></div>
                    <div className="md:col-span-3"><Skeleton height={38} borderRadius={8} /></div>
                    <div className="md:col-span-2"><Skeleton height={38} borderRadius={8} /></div>
                    <div className="md:col-span-2"><Skeleton height={38} borderRadius={8} /></div>
                  </div>
                ))}
              </SkeletonTheme>
            ) : items.length === 0 ? (
              <div className={`px-4 py-8 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No resources yet. Add one above.</div>
            ) : (
              items.map((it) => (
                <div key={it.id} className="relative flex flex-col gap-3 px-4 pt-10 pb-4 md:grid md:grid-cols-12 md:gap-3 md:items-center md:py-3">
                  <div className="md:col-span-5">
                    <span className={`md:hidden ${labelCls}`}>Title</span>
                    <input type="text" value={it.title || ""} onChange={(e) => updateItem(it.id, "title", e.target.value)} className={inputCls} />
                  </div>
                  <div className="md:col-span-3">
                    <span className={`md:hidden ${labelCls}`}>Placement</span>
                    <select value={it.placement} onChange={(e) => updateItem(it.id, "placement", e.target.value)} className={inputCls}>
                      {PLACEMENTS.map((pl) => <option key={pl.value} value={pl.value}>{pl.label}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <span className={`md:hidden ${labelCls}`}>Type</span>
                    <select value={it.type} onChange={(e) => updateItem(it.id, "type", e.target.value)} className={inputCls}>
                      {TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-1">
                    <span className={`md:hidden ${labelCls}`}>Source</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                      {it.source === "file" ? <FileText size={13} /> : it.source === "youtube" ? <Youtube size={13} /> : <Link2 size={13} />}
                      {srcLabel[it.source] || it.source}
                    </span>
                  </div>
                  <button type="button" onClick={() => setPendingDelete(it.id)} title="Delete resource"
                    className={`absolute top-2 right-2 md:static md:col-span-1 md:justify-self-end p-2 rounded-lg ${isDarkMode ? "text-rose-300 hover:bg-slate-700" : "text-rose-600 hover:bg-rose-50"}`}>
                    <Trash2 size={16} />
                  </button>
                  {/* The link of a link or YouTube resource can be changed here (an uploaded file is swapped by deleting and adding it again) */}
                  {it.source !== "file" && (
                    <div className="md:col-span-11">
                      <label htmlFor={`resource-url-${it.id}`} className={labelCls}>Link</label>
                      <input id={`resource-url-${it.id}`} type="url" value={it.url || ""} onChange={(e) => updateItem(it.id, "url", e.target.value)}
                        placeholder={it.source === "youtube" ? "YouTube link or video id" : "https://…"} className={inputCls} />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {!loading && items.length > 0 && (
            <div className={`flex justify-end px-4 py-3 border-t ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
              <button type="button" onClick={saveAll} disabled={!dirtyItems.length || saving}
                className="rounded-lg px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
                <Save size={16} /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {pendingDelete !== null && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => !deleting && setPendingDelete(null)} />
          <div className={`relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ${isDarkMode ? "bg-slate-800 border border-slate-700" : "bg-white"}`}>
            <div className="p-6">
              <h3 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Delete this resource?</h3>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setPendingDelete(null)} disabled={deleting}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${isDarkMode ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>Cancel</button>
                <button type="button" onClick={confirmDelete} disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-50">{deleting ? "Deleting…" : "Delete"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResources;
